/**
 * DEEP CELESTIAL RESEARCH, on the chart.
 *
 * <h2>A builder over the shared renderer, not a new one</h2>
 *
 * `chartDataProcessor`'s `pivotmarkers` branch already draws per-point symbols, rotation, colour,
 * placement, adjacent labels and per-point tooltips — and it is the chokepoint that forces a
 * projected mark to render as a gold circle rather than an outcome-coloured triangle. The Deep
 * Pivot tags go through it for the same reason. A second renderer here would duplicate all of
 * that and quietly opt out of the chokepoint.
 *
 * <h2>A NON-TRADING DAY HAS NO COLUMN AT ALL</h2>
 *
 * The price grid's x axis is a CATEGORY axis whose categories are the `YYYY-MM-DD` strings in
 * `barData.dates`. A Saturday is not one of them — so an aspect on a Saturday cannot be drawn at
 * its own date, and handing the renderer that date makes ECharts drop the point in silence.
 *
 * <p>So those are SNAPPED FORWARD to the next trading day and drawn higher, with a hollow symbol.
 * Forward because that is how a weekend aspect is acted on: it happened before this bar opened.
 * Two channels rather than one — height AND shape — because a screenshot read in greyscale, or by
 * a colour-blind reader, loses a hue but keeps a silhouette and a position.
 */

/** One id, so a re-run replaces the previous paint instead of stacking on it. */
export const CELESTIAL_OVERLAY_ID = 'celestial-aspect-markers';

/**
 * WHERE THE MARKS GO, and it is a question about meaning rather than taste.
 *
 * <p>ON THE BARS, a down-pointing triangle sits above each marked high — which is the universal
 * shorthand for a SHORT. These aspects carry no direction whatever; some of the bars they mark
 * turned up. The placement was implying a trade the study never makes.
 *
 * <p>ON A RAIL, every mark sits on one line at the foot of the panel with a thin vertical
 * through its bar. It says "this bar" and nothing about which way, which is what the study
 * actually knows.
 */
export const CELESTIAL_PLACEMENT = { BARS: 'BARS', RAIL: 'RAIL' };

/**
 * A muted indigo, distinct from everything else that can share this canvas: the Deep Pivot tags'
 * bronze #8B7355, the trade cards' navy and charcoal, and the yearly plaque's gold #D4A017.
 */
export const CELESTIAL_COLOR = '#4C5B8A';

/** Clear of the wick for a trading-day mark... */
export const CELESTIAL_OFFSET_PX = 14;
/** ...and clear of THOSE for one snapped forward from a non-trading day. */
export const CELESTIAL_SNAPPED_OFFSET_PX = 34;

const KIND_INITIAL = { CONJUNCTION: 'C', SQUARE: 'S', OPPOSITION: 'O', TRINE: 'T' };

/**
 * The index of the first trading day on or after a date.
 *
 * <p>A plain lookup would silently drop every weekend and holiday aspect — which is most of
 * them, two days in seven before holidays are counted. `dates` is sorted and in `YYYY-MM-DD`, so
 * a string comparison is a date comparison; parsing would reintroduce the timezone shift this
 * repository has been caught by before, to answer a question that needs no clock.
 *
 * @returns {{index: number, snapped: boolean}|null} null when the date is past the last bar
 */
export const snapForward = (dates, day) => {
  if (!Array.isArray(dates) || dates.length === 0 || typeof day !== 'string') return null;
  // Binary search: a full-history run matches thousands of days against ~8,500 bars, and a
  // linear scan per day is the difference between instant and visibly slow.
  let lo = 0;
  let hi = dates.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (dates[mid] >= day) { found = mid; hi = mid - 1; } else { lo = mid + 1; }
  }
  if (found < 0) return null;
  return { index: found, snapped: dates[found] !== day };
};

/** A short code for the aspects that matched, trine implicit. */
export const matchLabel = (match) => {
  const others = Array.isArray(match && match.others) ? match.others : [];
  if (others.length === 0) return KIND_INITIAL.TRINE;
  const initials = [...new Set(others.map((o) => KIND_INITIAL[o.kind] || '?'))].sort();
  return initials.join('');
};

/** The hover card: which trine, which other aspects, and the exact instants behind them. */
export const matchTooltipLines = (match, snapped, barDate) => {
  const line = (label, value) => `${String(label).padEnd(9)}${value}`;
  const at = (e) => `${e.pair} ${e.kindLabel} ${Math.round(e.angle)}°  ${e.instantUtc}`;
  const out = [line('Trine', at(match.trine))];
  (match.others || []).forEach((o, i) => out.push(line(i === 0 ? 'With' : '', at(o))));
  if (match.commonBody) {
    out.push(line('Common', `${match.commonBody} — stands in both`));
  } else if ((match.others || []).length > 0) {
    out.push(line('Common', 'none — the pairs share no body'));
  }
  if (snapped) {
    // Said explicitly. A mark drawn on a bar whose date is not the aspect's date is the one
    // thing here a reader could misread as a bug rather than a convention.
    out.push(line('Note', `fell on ${match.day}, a non-trading day — drawn on ${barDate}`));
  }
  return out;
};

/**
 * THE OVERLAY.
 *
 * @param report the scan response — `{ matches: [...] }`
 * @param barData the chart's own `{ dates, ohlc }`; ohlc is [open, close, low, high]
 * @returns an overlay carrying `drawnCount`, always — a null would make "nothing matched" and
 *          "never run" the same thing
 */
export const celestialAspectOverlay = (report, barData,
                                       placement = CELESTIAL_PLACEMENT.BARS) => {
  const matches = report && Array.isArray(report.matches) ? report.matches : [];
  const dates = (barData && Array.isArray(barData.dates)) ? barData.dates : [];
  const ohlc = (barData && Array.isArray(barData.ohlc)) ? barData.ohlc : [];

  const points = [];
  let snappedCount = 0;
  let pastEnd = 0;
  // THE COMPOSITION OF WHAT IS ACTUALLY DRAWN, counted here rather than inferred from the
  // report. The two differ: a match whose day falls past the last bar is counted by the report
  // and drawn nowhere, so a reader sizing a comparison from `matchCount` would overstate it by
  // exactly the marks that are not on the chart.
  let commonCount = 0;
  let differentCount = 0;

  matches.forEach((match) => {
    if (!match || typeof match.day !== 'string') return;
    const hit = snapForward(dates, match.day);
    if (!hit) { pastEnd += 1; return; }
    const bar = ohlc[hit.index];
    if (!Array.isArray(bar) || !Number.isFinite(bar[3])) { pastEnd += 1; return; }
    if (hit.snapped) snappedCount += 1;
    // `commonBody` set means the trine and its matched aspect share a body — exactly the split
    // the Common / Different checkboxes select on, so the baseline can mirror it.
    if (match.commonBody) commonCount += 1; else differentCount += 1;

    points.push({
      date: dates[hit.index],
      // The bar's HIGH, so the mark sits over the candle rather than inside it.
      price: bar[3],
      position: 'above',
      // FILLED for a trading day, HOLLOW for one snapped forward. The height already says it;
      // the silhouette says it again for a greyscale screenshot.
      symbol: hit.snapped ? 'emptyTriangle' : 'triangle',
      symbolRotate: 180,
      color: CELESTIAL_COLOR,
      label: matchLabel(match),
      title: `${match.trine.pair} Trine${match.commonBody ? ` · ${match.commonBody}` : ''}`,
      tooltipLines: matchTooltipLines(match, hit.snapped, dates[hit.index]),
      tooltipFooter: match.day,
      // DRAWN HIGHER when snapped, which is the brief's own way of telling the two apart. A
      // per-point offset rather than a second overlay: two overlays would need two ids, and the
      // caller clearing only one would leave half the study on the chart forever.
      offsetPx: hit.snapped ? CELESTIAL_SNAPPED_OFFSET_PX : CELESTIAL_OFFSET_PX,
      snapped: hit.snapped,
    });
  });

  const rail = placement === CELESTIAL_PLACEMENT.RAIL;
  return {
    // The SAME points either way. Only the renderer changes, so a reader switching placement is
    // looking at one answer drawn two ways rather than at two studies that might disagree.
    type: rail ? 'aspectRail' : 'pivotMarkers',
    id: CELESTIAL_OVERLAY_ID,
    placement,
    points,
    color: CELESTIAL_COLOR,
    label: 'Celestial aspects',
    // DELIBERATELY NOT `astroTooltip`. That flag gates on `suppressAstroTooltips`, which is
    // `shortBarMode || barFloaterMode === 'ohlcv'` -- and the floater mode DEFAULTS to 'ohlcv',
    // so opting in suppresses the card for everybody out of the box. Setting it here is exactly
    // the mistake the spiral arcs already made and recorded; short-bar mode is enforced
    // absolutely in the renderer instead, on `shortBarMode` itself.
    source: 'celestialAspects',
    // ADMIN RESEARCH, so the short-bar preset does not hide the hover card. That preset is a
    // customer-facing constraint — "nothing astrological reaches the screen" — and this dialog
    // returns null outright for a non-admin, so there is no customer to constrain. Without it
    // the triangles and the rail both draw and neither answers a hover, which reads as broken
    // rather than as restricted. See chartDataProcessor's `researchTooltip`.
    researchTooltip: true,
    symbol: 'triangle',
    symbolSize: 10,
    symbolOffsetPx: CELESTIAL_OFFSET_PX,
    labelFontSize: 10,
    labelFontFamily: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
    drawnCount: points.length,
    snappedCount,
    pastEndCount: pastEnd,
    // WHAT THE RANDOM BASELINE SIZES ITSELF FROM. See celestialRandomBaseline — the baseline
    // draws exactly this many marks, split exactly this way, so the two pictures are comparable.
    commonCount,
    differentCount,
  };
};

/** What to put in the status line. Names what was NOT drawn, which is the half that gets lost. */
export const celestialStatus = (report, overlay) => {
  if (!overlay || overlay.drawnCount === 0) {
    return 'Celestial aspects: nothing matched the current selection';
  }
  const bits = [`Celestial aspects: ${overlay.drawnCount} days marked`];
  if (overlay.snappedCount) {
    bits.push(`${overlay.snappedCount} snapped forward from non-trading days`);
  }
  if (overlay.pastEndCount) bits.push(`${overlay.pastEndCount} past the last bar`);
  if (report && Number.isFinite(report.eventCount)) {
    bits.push(`${report.eventCount} aspects in range`);
  }
  return bits.join(' · ');
};
